import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { combineLatest, Observable, of } from "rxjs";
import { Tradable } from "@models/interfaces/tradable";
import { Fiat } from "@models";
import { ASSET_LIST_CURRENCY_TYPE_FILTERS } from "@modules/trade/constants/constants";
import { AccountService } from "@modules/account/services/account.service";
import { Index } from "@models/asset/index/index.model";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { convertTradableToFiat, toCurrencyByFiatId } from "@helpers/tradable-convert";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { AssetListSellService } from "@modules/trade/services/asset-list-sell.service";
import { BpOption } from "@models/ui-components/bp-option";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { map, take } from "rxjs/operators";
import { INDEX_TYPE, LEVERAGED_TOKEN_TYPE, SECURITY_ASSET_GROUP_LIST, TRADABLES_TYPE } from "@helpers/constants";
import { config } from "@config";
import { RealStocksFacade } from "@modules/real-stocks-temp/real-stocks.facade";
import { DYNAMIC_CONFIGS, RS_REDIRECTION_TYPE, StatsigClientService } from "@core-services/statsig/statsig.service";

@Component({
	selector: "sell-asset-list",
	templateUrl: "./sell-asset-list.component.html",
	styleUrls: ["./sell-asset-list.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [AssetListSellService],
	standalone: false
})
export class SellAssetListComponent implements OnInit {
	@Input() public tradables$: Observable<Tradable[]> = this.assetListSellService.getList$();
	@Output() public assetClicked: EventEmitter<Tradable> = new EventEmitter<Tradable>();
	@Input() public isLimitOrder: boolean = false;

	public defaultFiat: Fiat = this.accountService.getDefaultFiat();
	public scrollToTop: boolean = false;
	public searchValue: string = "";
	public filterValue: string = "";
	public filters: BpOption[] = ASSET_LIST_CURRENCY_TYPE_FILTERS;
	private areLeverageActive$: Observable<boolean> = this.launchDarklyService.isActive$(LD_FLAG.LEVERAGE);
	private isSecurityTokenActive$: Observable<boolean> = this.launchDarklyService.isActive$(LD_FLAG.SECURITY_TOKENS);
	private realSecuritiesRedirectionType$ = this.statsigService.getDynamicConfig<Record<"value", RS_REDIRECTION_TYPE>>(
		DYNAMIC_CONFIGS.RS_REDIRECTION_TYPE
	);

	public pillsThatNeedRealSecuritiesBanner = SECURITY_ASSET_GROUP_LIST;

	public ownedLegacyAssets = 0;
	public REDIRECTION_TYPE = RS_REDIRECTION_TYPE;

	public filterListFF$ = combineLatest([
		this.areLeverageActive$,
		this.isSecurityTokenActive$,
		of(this.filters),
		this.tradables$.pipe(take(1))
	]).pipe(
		map(([activeL, activeSecurityTokens, filters, tradables]) => {
			filters = this.assetListSellService.getFilters(filters, tradables);

			if (!activeL) {
				return filters.filter((f) => f.value !== LEVERAGED_TOKEN_TYPE);
			}

			if (!activeSecurityTokens) {
				return filters.filter((f) => f.value !== TRADABLES_TYPE.SECURITY_TOKEN);
			}
			return filters;
		})
	);

	constructor(
		private readonly accountService: AccountService,
		private readonly assetListSellService: AssetListSellService,
		private readonly tradableWalletListService: TradableWalletListService,
		private readonly launchDarklyService: LaunchDarklyService,
		private readonly realStocksFacade: RealStocksFacade,
		private readonly statsigService: StatsigClientService
	) {}

	public ngOnInit(): void {
		this.assetListSellService.initList(this.isLimitOrder);
		this.tradables$.pipe(take(1)).subscribe((tradables) => {
			this.ownedLegacyAssets = tradables.filter(this.tradableIsLegacy).length;
		});
	}

	public onClick(tradable: Tradable): void {
		this.assetClicked.emit(tradable);
	}

	public onFilterChange(option: BpOption): void {
		this.filterValue = option.value;
		this.filterUpdate(this.filterValue);
	}

	public onSearchValueChange(val: string): void {
		this.searchValue = val;
		this.assetListSellService.filterAndSearchList(this.filterValue, val);
		this.scrollToTop = true;
	}

	public filterUpdate(filter: string): void {
		this.filterValue = filter;
		this.assetListSellService.filterAndSearchList(filter, this.searchValue);
		this.scrollToTop = true;
	}

	public getWalletSumFiat(tradable: Tradable): number {
		return this.calculateWalletSum(this.getWalletSum(tradable), tradable);
	}

	public getWalletSum(c: Tradable): number {
		return this.getWallets(c).reduce((val: number, w: TradableWallet) => val + w.getBalance(), 0);
	}

	private calculateWalletSum(amount: number, tradable: Tradable): number {
		if (tradable instanceof Index) {
			return toCurrencyByFiatId(amount, this.defaultFiat);
		}
		return convertTradableToFiat(tradable, amount, this.accountService.getDefaultFiat());
	}

	private getWallets(c: Tradable): TradableWallet[] {
		return this.tradableWalletListService.list.filter(
			(w: TradableWallet) => w.getCurrencyId() === c.getId() && !w.isDeleted()
		);
	}

	public isIndex(tradable: Tradable): boolean {
		return tradable instanceof Index;
	}

	protected readonly INDEX_TYPE = INDEX_TYPE;

	protected tradableIsLegacy(tradable: Tradable): boolean {
		return (
			tradable.getCurrencyType() === TRADABLES_TYPE.STOCK ||
			tradable.getCurrencyType() === TRADABLES_TYPE.ETF ||
			tradable.getCurrencyType() === TRADABLES_TYPE.ETC
		);
	}

	protected getRSFeeText(): string {
		return this.realStocksFacade.getRSFeeFormatted();
	}

	public getV4RedirectURL(): string {
		return config.V4AppBaseUrl + "/?o=modal-sell&stepId=AssetSelection";
	}
}
